import { useCallback, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useDropzone } from 'react-dropzone'
import { Path, useFormContext } from 'react-hook-form'
import { Button, Image, Link, ListItem, Loader, Text } from '@/client/components'
import { Icon } from '@/client/components/Icon'
import { useRouterPush } from '@/client/hooks'
import { useFileUpload } from '@/client/hooks/useChunkUpload'
import { getFileResult, uploadFile } from '@/infra/services/uploadFile'
import { Params } from '@/infra/services/uploadFile/types'
import * as S from './styles'
import { bufferType, FormSchema, UpdateFifo } from './types'

export const UploadPDF = ({
  disabled,
  upload_button,
  upload_text,
  drag_and_drop,
  cID,
  width,
  height,
  opaque,
  foil,
  preflight_mistakes,
  professional_assistance,
  artwork_service_long_text,
  artwork_service_button,
  artwork_service_image,
  preflight_success,
  success_long_text,
  calculator_button,
  calculator_link,
  artwork_service_link,
  first_waiting_text,
  second_waiting_text
}) => {
  const jobsName: Path<FormSchema> = 'uploadedFiles'
  const { setValue, watch, getValues } = useFormContext<FormSchema>()
  const buffer = watch(jobsName, [])
  const [page, setpage] = useState<number | undefined>(0)
  const [hash, sethash] = useState<string | undefined>('')

  const [filteredBuffer, setFilteredBuffer] = useState<bufferType>(buffer)
  const { locale } = useRouterPush()

  const queryClient = useQueryClient()

  const controlledFifo = (newUpload: boolean) => {
    if (newUpload && status === 'ongoing') return
    const params: Params = {
      cID: cID,
      job: '',
      preflight: '1',
      check_hotfoil: foil,
      check_opaque: opaque,
      width: width,
      height: height
    }
    const fifo = getValues(jobsName)
    for (const item of fifo) {
      const { file, status } = item
      if (status === 'pending') {
        updateFifo({ name: file.name, status: 'ongoing' })
        addFile({ newFile: file, props: params })
        return
      }
    }
  }

  const updateFifo = (data: UpdateFifo) => {
    const fifo = getValues(jobsName)
    const item = fifo.find(({ file: { name } }) => name === data.name)
    if (!item) return
    item.status = data.status
    item.response = data.response
    setValue(jobsName, fifo)
    setFilteredBuffer(fifo)
    setpage(data.response?.page)
    sethash(data.response?.preflight_hash)
  }

  const { mutateAsync } = useMutation({ mutationFn: uploadFile, retry: 1000 })

  const { addFile, progress, abort, status, currentItem } = useFileUpload({
    queryFn: mutateAsync,
    onFinally: ({ item, response }) => {
      updateFifo({
        name: item?.file?.name ?? '',
        status: response ? 'finished' : 'fail',
        response: response
      })
      controlledFifo(false)
    }
  })

  const upload = async (files: FileList | File[]) => {
    const buffetTemp: FormSchema['uploadedFiles'] = []
    for (const current of files) {
      const exists = buffer.find(({ file }) => file.name === current.name)
      if (!exists && current.type === 'application/pdf') {
        buffetTemp.push({ file: current, status: 'pending' })
      }
    }
    const newBuffer = [...buffer, ...buffetTemp]
    setValue(jobsName, newBuffer)
    controlledFifo(true)
    setFilteredBuffer(newBuffer)
  }

  //for drag and drop
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: upload,
    accept: { 'application/pdf': [] },
    multiple: true
  })

  const deleteFile = (name?: string) => {
    if (currentItem?.file?.name === name) abort()
    const filter = buffer.filter(({ file }) => file.name !== name)
    setValue(jobsName, filter)
    setFilteredBuffer(filter)
    queryClient.invalidateQueries(['preview-result'])
  }

  // for preview
  const PreView = useCallback(
    (index: number) => {
      const item = buffer[index]
      if (item?.status === 'finished' && item?.response?.preview) {
        return (
          <S.PreviewContainer key={item.file.name}>
            <Button size='small' onClick={() => deleteFile(item?.file.name)} className='delete-button'>
              <Icon name='close-circle-s' size='s5' />
            </Button>
            <Image
              src={item?.response?.preview}
              alt={item?.response?.finalFilename || 'Preview'}
              width='80'
              height='60'
            />
            <Text margin='0' padding='0' color='weak' className='preview-filename'>
              {item?.response?.finalFilename}
            </Text>
          </S.PreviewContainer>
        )
      }
      return null
    },
    [buffer]
  )

  // To show the result
  let { current: count } = useRef<number>(0)
  const { data, isLoading } = useQuery({
    queryKey: ['preview-result', filteredBuffer],
    queryFn: async () => {
      const results = await Promise.all(
        buffer.map(({ response, status }) => {
          if (status === 'finished' && response?.preflight_hash && response?.page) {
            return getFileResult(locale, response.preflight_hash, response.page, width, height)
          }
          return null
        })
      )
      return results.filter((result) => result !== null)
    },
    enabled: !!hash && !!page,
    keepPreviousData: true,
    refetchInterval: (data) => {
      if (!data || data.length === 0) return false
      const anyPending = data.some((item) => (item?.pending ?? 0) > 0)
      if (!anyPending) return false
      if (count >= 30) return false
      count = count + 1
      return 2000
    }
  })

  return (
    <S.Container>
      <S.UploadContainer {...(disabled ? {} : getRootProps())}>
        <S.HiddenInput
          multiple
          accept='application/pdf'
          onChange={(event) => event.target.files && upload(event.target.files)}
          {...getInputProps()}
        />
        <S.Option disabled={disabled}>
          <Button disabled={disabled} className='upload-button'>
            <Icon name='upload-dark' />
            {upload_button}
          </Button>
          <Text lineHeight='1.2' padding='0.5rem'>
            <span dangerouslySetInnerHTML={{ __html: upload_text }} />
          </Text>
        </S.Option>

        {isLoading && !progress && (
          <S.DragContainer disabled={disabled}>
            <Icon name='upload-new' size='s9max' className='upload-icon' />
            <Text family='heading' align='center' size='xxlg' weight='semiBlack' className='drag-text'>
              <span dangerouslySetInnerHTML={{ __html: drag_and_drop }} />
            </Text>
          </S.DragContainer>
        )}
      </S.UploadContainer>
      {status === 'ongoing' && progress === 0 && (
        <>
          <Loader type='spinner' size='medium' wrapper='4rem' color='info' />
          <Text weight='bold' align='center' style={{ color: '#4E5CA5' }}>
            {first_waiting_text}
          </Text>
        </>
      )}
      <>
        {data?.map((item, index) => {
          if (item === null) return null

          const { pending, result, errors } = item

          return (
            <>
              {pending === 1 && (
                <>
                  <Loader type='spinner' size='medium' wrapper='4rem' color='warning' />
                  <Text weight='bold' color='warning' align='center'>
                    {second_waiting_text}
                  </Text>
                </>
              )}

              {pending === 0 && (
                <>
                  <S.DividerLine />
                  {PreView(index)}
                  {result ? (
                    <>
                      <S.SucessResult>
                        <Text lineHeight='1.3' as='span'>
                          {preflight_success}{' '}
                          <span dangerouslySetInnerHTML={{ __html: success_long_text }} className='sucess-text' />
                        </Text>
                      </S.SucessResult>
                    </>
                  ) : (
                    <>
                      <Text lineHeight='1.4'>{preflight_mistakes}</Text>
                      <S.ErrorResult>
                        {errors?.map((error) => (
                          <ListItem key={error} iconName='cross' className='error-list-item'>
                            <Text as='span' lineHeight='1.4' className='error-list-item' size='sm'>
                              {error}
                            </Text>
                          </ListItem>
                        ))}
                      </S.ErrorResult>
                    </>
                  )}
                </>
              )}
            </>
          )
        })}

        {Array.isArray(data) && data.length > 0 && data.every((item) => item?.result && item.pending === 0) && (
          <>
            <S.DividerLine />
            <Link href={calculator_link}>
              <Button kind='warning' style={{ marginTop: '1rem' }}>
                {calculator_button}
                <Icon name='arrow-right' size='s7' />
              </Button>
            </Link>
          </>
        )}

        {data?.some((item) => !item?.result && item?.pending === 0) && (
          <>
            <S.DividerLine />
            <Text color='weak' lineHeight='1.2' size='sm' padding='1rem 0 0 0'>
              {professional_assistance}
            </Text>
            <S.ArtworkService>
              <Icon name='artwork-service1' size='s6' className='artwork-service-icon' />
              <div>
                <Text color='weak' lineHeight='1.2' size='xs' padding='1rem 0 0 0'>
                  <span dangerouslySetInnerHTML={{ __html: artwork_service_long_text }} />
                </Text>
                <Link href={artwork_service_link}>
                  <Button kind='warning' size='small' style={{ borderRadius: '2rem', height: '1.2rem' }}>
                    {artwork_service_button}
                  </Button>
                </Link>
              </div>
              <div>
                <Image src={artwork_service_image.url} width='70' height='90' />
              </div>
            </S.ArtworkService>
          </>
        )}
      </>
    </S.Container>
  )
}
